import { cn } from '@invent/wl-ui-kit-next';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';
import { Link } from 'react-router-dom';

import { StatusChip } from '@/design';
import { useCan } from '@/domains/auth';
import { formatDateTime } from '@/domains/survey';
import type { EmailChangeReason } from '../../api/account-contracts';
import type { IParticipantMailStatus } from '../../api/mail-log-contracts';
import { mailLogApi } from '../../api/mail-log-api';
import type { IAdminActivityRow } from '../../api/participant-contracts';
import { participantsApi } from '../../api/participants-api';
import { absent, accountKindWords } from '../../components/account-facts';
import { EMAIL_CHANGE_REASONS } from '../../components/login-address-card';
import {
  LoadError,
  Pager,
  PastTheEnd,
  Section,
  TableSkeleton,
  useAdminData
} from '../../components/admin-page';
import { PayloadTree } from '../../components/payload-tree';
import { TableFrame } from '../../components/table-frame';
import { mailLogPath } from '../../paths';
import { kindLabel as mailKindLabel, reasonWords, sourceLabel } from '../mail-log/mail-words';
import { ClearSuppressionDialog, ReplyDrawer } from '../mail-log/suppression-dialogs';
import { useListPaging } from './client-page';

/**
 * What happened to the household, newest first (R12) — survey-api's
 * `GET …/participants/{id}/activity`: its accounts' `account_event` rows, every type, and its
 * members' participant-scoped `audit_event` rows, merged and paged by survey-api. Which rows
 * those are is survey-api's to decide — the accounts linked **now**, so an unlinked account's
 * history leaves the feed with it — and nothing here filters or joins.
 *
 * **Its own read, not the hub's**, and paged by the server, as the Salesforce tasks are since
 * SR-216: the feed has no bound, where the hub's responses and notes are bounded by the
 * household. So it loads and fails on its own, below a hub already on screen, with its own three
 * arms, and a failed later read keeps the page it had. It is **not** polled — the poll is for a
 * registry's answer, which arrives on the hub — and is re-read when the hub re-reads after a
 * write (`written`), since a verify, a press, a link or an unlink is an event of its own.
 *
 * **A kind is survey-api's spelling, labelled where this app knows it.** The label is words for
 * a reader; the stored type is drawn beside it, so a label never hides what was stored, and a
 * kind with no label here is drawn by its spelling alone — a new event type in survey-api is
 * still an event, not a gap. The alternative, a closed union of kinds on the wire type, would
 * turn the first new one into a row this app cannot draw.
 *
 * **Who** is the actor's address; an actor whose account has since gone is said to be one
 * (survey-api keeps the id and loses the address); no actor — a system event, a refused sign-in
 * — draws nothing. The event's stored `detail` is folded under the row, whole, in `PayloadTree`:
 * survey-api keeps addresses, IPs and user agents out of it, and which of its members matter is
 * not this app's reading to make.
 */
export function ActivitySection({
  participantId,
  written,
  anchor,
  onTotal
}: {
  participantId: string;
  /** Bumped by every write's re-read of the hub — the activity's cue to read again. */
  written: number;
  anchor: string;
  /** Told survey-api's `total` each time a read lands — the section nav's count. */
  onTotal?: (total: number) => void;
}) {
  // The groups shown (SR-407): none pressed is every group. Survey-api narrows the feed, so the
  // pager and the total are of what is shown, never one page filtered here.
  const [groups, setGroups] = React.useState<string[]>([]);
  const toggle = (group: string) =>
    setGroups((now) =>
      now.includes(group) ? now.filter((shown) => shown !== group) : [...now, group]
    );
  return (
    <Section title="Activity" anchor={anchor}>
      <CardContent className="grid gap-4">
        <MailStrip participantId={participantId} written={written} />
        <div role="group" aria-label="Show" className="flex flex-wrap items-center gap-1.5">
          <span className="pr-1 text-xs text-muted-foreground">Show</span>
          <GroupChip pressed={groups.length === 0} onPress={() => setGroups([])}>
            All
          </GroupChip>
          {ACTIVITY_GROUPS.map(([group, words]) => (
            <GroupChip
              key={group}
              pressed={groups.includes(group)}
              onPress={() => toggle(group)}
            >
              {words}
            </GroupChip>
          ))}
        </div>
        {/* Remounted per choice, so a page read under one choice is never drawn under another
            and the page starts again at the first. */}
        <ActivityFeed
          key={groups.join(' ')}
          participantId={participantId}
          written={written}
          groups={groups}
          // The nav counts the whole feed: a narrowed total would say the section holds less.
          onTotal={groups.length === 0 ? onTotal : undefined}
        />
      </CardContent>
    </Section>
  );
}

/** survey-api's activity groups (`ActivityGroups`), in words — its codes, which group each
 *  kind is in being its decision, not this app's. */
const ACTIVITY_GROUPS: [string, string][] = [
  ['sign_in', 'Sign-ins'],
  ['account', 'Account & access'],
  ['record', 'Record changes'],
  // SR-523 stage 3: every `mail_*` event — sent, skipped, bounced, unsubscribed, replied.
  ['mail', 'Email']
];

function GroupChip({
  pressed,
  onPress,
  children
}: {
  pressed: boolean;
  onPress: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={pressed}
      onClick={onPress}
      className={cn(
        'rounded-full border px-2.5 py-0.5 text-xs transition-colors',
        pressed
          ? 'border-[var(--color-teal)] bg-[var(--accent)] font-medium text-[var(--color-royal-blue)]'
          : 'border-border text-muted-foreground hover:text-[var(--color-royal-blue)]'
      )}
    >
      {children}
    </button>
  );
}

function ActivityFeed({
  participantId,
  written,
  groups,
  onTotal
}: {
  participantId: string;
  written: number;
  groups: string[];
  onTotal?: (total: number) => void;
}) {
  const { page, pageSize, setPage, more } = useListPaging();
  const { data, error, reload } = useAdminData(
    (signal) =>
      participantsApi.listActivity(participantId, { page, pageSize, group: groups }, signal),
    [participantId, page, pageSize, written]
  );
  // The pager is drawn from the page last read, not the page asked for — a result is drawn only
  // under the page it answers — so after a failed read of page 2 it still offers "Next" to page
  // 2, which is `page` already: setting it again would change nothing and ask for nothing, and
  // the button would be dead until a write. A press for the page already asked for asks again.
  const turnTo = (target: number) => (target === page ? void reload() : setPage(target));
  const total = data?.total;
  React.useEffect(() => {
    if (total !== undefined) onTotal?.(total);
  }, [total, onTotal]);

  return (
    <>
      {error &&
        (data === null ? (
          <LoadError message={error} />
        ) : (
          <Alert variant="destructive">
            <AlertTitle>Could not refresh the activity — showing it as last read</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ))}
      {data === null ? (
        error ? null : <TableSkeleton rows={3} />
      ) : (
        <>
          <ActivityTable
            rows={data.items}
            empty={
              // A re-read after a write can shrink the feed under an open later page — an
              // unlink takes an account's events with it — and the feed is not empty then.
              data.total > 0 ? (
                <PastTheEnd
                  page={data.page}
                  pageSize={data.pageSize}
                  total={data.total}
                  counted={['event', 'events']}
                  onPage={turnTo}
                />
              ) : groups.length > 0 ? (
                'Nothing of these kinds is recorded for this household.'
              ) : (
                'Nothing is recorded for this household.'
              )
            }
          />
          <Pager
            page={data.page}
            pageSize={data.pageSize}
            total={data.total}
            onPage={turnTo}
            quiet
            onMore={more}
          />
        </>
      )}
    </>
  );
}

function ActivityTable({ rows, empty }: { rows: IAdminActivityRow[]; empty: React.ReactNode }) {
  const [reply, setReply] = React.useState<Nullable<string>>(null);
  return (
    <TableFrame>
      <ReplyDrawer replyId={reply} onClose={() => setReply(null)} />
      <Table aria-label="Activity">
        <TableHeader>
          <TableRow>
            <TableHead>When</TableHead>
            <TableHead>Event</TableHead>
            <TableHead>Who</TableHead>
            <TableHead>Detail</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={`${row.source}:${row.id}`}>
              <TableCell className="text-xs whitespace-nowrap text-muted-foreground">
                {formatDateTime(row.at)}
              </TableCell>
              <TableCell>
                <Kind kind={row.kind} label={kindLabel(row)} />
              </TableCell>
              <TableCell className="break-all">
                {row.actor ? (
                  (row.actor.email ?? absent('an account since removed'))
                ) : (
                  <AutomaticLink row={row} />
                )}
              </TableCell>
              <TableCell>
                {row.kind === 'role_changed' && <RoleChange detail={row.detail} />}
                <EmailChange row={row} />
                <StatusChange row={row} />
                <AccessChange row={row} />
                <MailEvent row={row} onReply={setReply} />
                {row.detail !== null && (
                  <details>
                    <summary className="cursor-pointer text-sm text-muted-foreground select-none">
                      As stored
                    </summary>
                    <div className="mt-2">
                      <PayloadTree value={row.detail} label="The event as stored" />
                    </div>
                  </details>
                )}
              </TableCell>
            </TableRow>
          ))}
          {rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={4} className="py-8 text-center text-muted-foreground">
                {empty}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableFrame>
  );
}

/**
 * A link no account made (SR-414, record search §4): the migration's `accountlinks` stage — and
 * survey-api's automatic link — tie a profile no account acts for to the account its email names,
 * and record `participant_linked` with no actor and `detail: {by: "email", address, …}`. Said as
 * what it was, the address drawn as sent where the detail holds one — never as "an account since
 * removed", which a null actor email means, nor as nothing. Any other actor-less row draws
 * nothing, as before. Staff's Unlink undoes it like any link.
 *
 * A team's or firm's roster links and unlinks the accounts it names (SR-487, survey-api's PFR
 * §5): `participant_linked` and `participant_unlinked` with no actor and `detail: {relation:
 * "member", by: "roster"}`, whose who is "Roster".
 */
function AutomaticLink({ row }: { row: IAdminActivityRow }) {
  const detail = detailOf(row);
  if (detail === null) return null;
  const linking = row.kind === 'participant_linked' || row.kind === 'participant_unlinked';
  if (linking && detail.by === 'roster') return <span data-roster-link>Roster</span>;
  if (row.kind !== 'participant_linked' || detail.by !== 'email') return null;
  return (
    <span data-automatic-link>
      Linked automatically — the address matched
      {typeof detail.address === 'string' && detail.address !== '' && <>: {detail.address}</>}
    </span>
  );
}

/**
 * `role_changed`'s `{ from, to, kind? }` (SR-228, `AccountRoleService`) — the role codes before
 * and after, and for a change of portal the account's kind before and after: survey-api writes
 * both writes as this one event. The one event whose values are said here, since they are what
 * changed: the kind in the portal's words, the roles as their codes and only where the two sets
 * differ (a kind's change carries the same set on both sides). A detail of another shape draws
 * nothing here, and the stored event below it still says it whole.
 */
function RoleChange({ detail }: { detail: unknown }) {
  if (detail === null || typeof detail !== 'object') return null;
  const record = detail as Record<string, unknown>;
  const codes = (value: unknown) =>
    Array.isArray(value) && value.every((code) => typeof code === 'string')
      ? (value as string[])
      : null;
  const side = (list: string[]) =>
    list.length === 0 ? absent('no role') : <code className="text-xs">{list.join(', ')}</code>;
  const from = codes(record.from);
  const to = codes(record.to);
  const rolesMoved =
    from !== null &&
    to !== null &&
    (from.some((code) => !to.includes(code)) || to.some((code) => !from.includes(code)));
  const kind = kindChange(record.kind);

  return (
    <>
      {kind && (
        <p className="mb-1 text-sm" data-kind-changed>
          Account kind changed: {accountKindWords(kind.from)} account →{' '}
          {accountKindWords(kind.to)} account
        </p>
      )}
      {rolesMoved && (
        <p className="mb-1 text-sm" data-roles-changed>
          {side(from)} → {side(to)}
        </p>
      )}
    </>
  );
}

/** `role_changed`'s `kind` member — `{ from, to }`, both strings — or `null`. */
function kindChange(value: unknown): Nullable<{ from: string; to: string }> {
  if (value === null || typeof value !== 'object') return null;
  const { from, to } = value as Record<string, unknown>;
  return typeof from === 'string' && typeof to === 'string' ? { from, to } : null;
}

/** A row's detail as an object, or `null` — the members a label or a line reads. */
function detailOf(row: IAdminActivityRow): Nullable<Record<string, unknown>> {
  return row.detail !== null && typeof row.detail === 'object'
    ? (row.detail as Record<string, unknown>)
    : null;
}

/**
 * A row's label: its kind's, or, for the two kinds SR-416 gave a second meaning, the one its
 * detail names (F5) — a link an accepted invitation made (`relation: "contact"`) is a point of
 * contact's, and an account it made carries `invitationId`. An account's status change (SR-442)
 * is named by the status it took. A roster's link and unlink (`relation: "member"`, SR-487) are
 * the listing's. Words only, the stored kind beside.
 */
function kindLabel(row: IAdminActivityRow): string | undefined {
  const detail = detailOf(row);
  if (row.kind === 'status_changed' && detail?.to === 'disabled') return 'Account disabled';
  if (row.kind === 'status_changed' && detail?.to === 'active') return 'Account activated';
  if (row.kind === 'participant_linked' && detail?.relation === 'contact') {
    return 'Linked as point of contact';
  }
  if (row.kind === 'participant_linked' && detail?.relation === 'member') {
    return 'Listed on the roster';
  }
  if (row.kind === 'participant_unlinked' && detail?.relation === 'member') {
    return 'No longer listed on the roster';
  }
  if (row.kind === 'account_created' && typeof detail?.invitationId === 'string') {
    return 'Account made from an invitation';
  }
  return KIND_LABELS[row.kind];
}

/**
 * The login address's change (SR-416, F5): `email_changed`'s `{from, to}`, said as the move, and
 * its `reason` (SR-487) in the words staff chose it by, a spelling this app does not know as
 * sent; and `email_change_requested`'s `{suppressed: true}`, the worker's mark that it withheld
 * the mail. A detail of another shape draws nothing here; the stored event below says it whole.
 */
function EmailChange({ row }: { row: IAdminActivityRow }) {
  const detail = detailOf(row);
  if (detail === null) return null;
  if (row.kind === 'email_changed') {
    const { from, to, reason } = detail;
    if (typeof from !== 'string' || typeof to !== 'string') return null;
    return (
      <>
        <p className="mb-1 text-sm break-all" data-email-changed>
          {from} → {to}
        </p>
        {typeof reason === 'string' && (
          <p className="mb-1 text-sm" data-email-reason>
            Reason: {EMAIL_CHANGE_REASONS[reason as EmailChangeReason] ?? reason}
          </p>
        )}
      </>
    );
  }
  if (row.kind === 'email_change_requested' && detail.suppressed === true) {
    return <p className="mb-1 text-sm">The mail was withheld.</p>;
  }
  return null;
}

/**
 * An account's status change (SR-442): `status_changed`'s `{from, to, reason}` — the reason staff
 * gave, said as typed; the move itself is the row's label. A detail without a reason draws nothing
 * here; the stored event below says it whole.
 */
function StatusChange({ row }: { row: IAdminActivityRow }) {
  const reason = row.kind === 'status_changed' ? detailOf(row)?.reason : undefined;
  if (typeof reason !== 'string' || reason.trim() === '') return null;
  return (
    <p className="mb-1 text-sm break-words" data-status-reason>
      Reason: {reason}
    </p>
  );
}

/**
 * A pin's access list changed (SR-487): `cycle_access_added`'s and `cycle_access_removed`'s
 * `{surveyCode, cycleCode}`, the pin named as the Cycles page names it. A detail of another shape
 * draws nothing here; the stored event below says it whole.
 */
function AccessChange({ row }: { row: IAdminActivityRow }) {
  if (row.kind !== 'cycle_access_added' && row.kind !== 'cycle_access_removed') return null;
  const detail = detailOf(row);
  if (typeof detail?.surveyCode !== 'string' || typeof detail.cycleCode !== 'string') return null;
  return (
    <p className="mb-1 text-sm" data-access-pin>
      {detail.surveyCode} · {detail.cycleCode}
    </p>
  );
}

/** A kind by its label, the stored spelling beside it — or by its spelling alone. */
function Kind({ kind, label }: { kind: string; label: string | undefined }) {
  const spelling = <code className="text-xs text-muted-foreground">{kind}</code>;
  if (label === undefined) return spelling;
  return (
    <span className="flex flex-wrap items-baseline gap-x-2">
      <span className="text-sm">{label}</span>
      {spelling}
    </span>
  );
}

/**
 * Words for the event types survey-api writes into this feed as of SR-215 — `AccountEvent`'s
 * types and the participant-scoped audit events — and SR-222's block 8, the photo set and
 * removed. Words only: nothing branches on a kind, and one missing here is drawn by its spelling;
 * nothing is drawn from an event's values either (a photo's blob name says nothing to a reader),
 * but for `role_changed`'s role codes and kinds (`RoleChange`), the login address's change
 * (`EmailChange`, SR-416), the reason an account's status changed (`StatusChange`, SR-442) and
 * the pin whose access list changed (`AccessChange`, SR-487). `role_changed` keeps its label for
 * a change of portal too: survey-api writes that as the same event. Four kinds read a member for
 * their label (`kindLabel`).
 */
const KIND_LABELS: Readonly<Record<string, string>> = {
  account_created: 'Account created',
  login_succeeded: 'Signed in',
  login_failed: 'Sign-in refused',
  logout: 'Signed out',
  password_changed: 'Password changed',
  password_reset_requested: 'Reset link requested',
  password_reset_consumed: 'Reset link used',
  password_reset_forced: 'Password reset by an administrator',
  password_change_required: 'Password change required',
  role_changed: 'Roles changed',
  // SR-442's: staff disabled the account or made it active again — named by the status it took
  // (`kindLabel`), this the words for a status this app does not know.
  status_changed: 'Account status changed',
  // SR-476's: an invitation to sign in at the IdP, and the identity it links. The password events
  // above keep their words: the feed still holds them, and history keeps its vocabulary.
  idp_invitation_created: 'Invited to sign in at Invent',
  idp_invitation_withheld: 'Invitation to sign in withheld',
  idp_invitation_requested: 'Asked for an invitation to sign in',
  idp_linked: 'Identity linked',
  idp_unlinked: 'Identity unlinked',
  email_verification_requested: 'Address confirmation requested',
  email_verified: 'Address confirmed',
  // SR-416's login address change (F5): asked for (the worker writes it, mailing the new address)
  // and made — by staff, or by the owner's link.
  email_change_requested: 'Asked to change the login address',
  email_changed: 'Login address changed',
  participant_linked: 'Linked to a participant',
  participant_unlinked: 'Unlinked from a participant',
  identifier_verified: 'Number verified',
  // SR-487's: a number check decided by staff (Verify is `identifier_verified`'s).
  identifier_rejected: 'Number rejected',
  identifier_replaced: 'Number replaced',
  registry_lookup_requested: 'Registry asked again',
  identifier_self_confirmed: 'Number confirmed by its owner',
  identifier_self_denied: 'Number denied by its owner',
  // F7's (SR-222, R8): Veritant's decision on a team's or firm's slot — on the team's feed, since
  // survey-api writes it under the team's id; `member` is an id, drawn as stored.
  roster_member_set: 'Roster member decided',
  roster_member_cleared: 'Roster decision cleared',
  photo_set: 'Photo set',
  photo_cleared: 'Photo removed',
  // SR-487's: a pin's access list (`AccessChange` names the pin), and a start its gate refused.
  cycle_access_added: 'Added to an access list',
  cycle_access_removed: 'Removed from an access list',
  cycle_access_refused: 'Start refused — not on the access list',
  // SR-523 stage 3's mail events (`MailEvent` names the email and why).
  mail_sent: 'Email sent',
  mail_failed: 'Email failed',
  mail_skipped: 'Email not sent',
  mail_bounced: 'Email bounced',
  mail_complaint: 'Email marked as spam',
  mail_suppressed: 'Unsubscribed from bulk email',
  mail_suppression_cleared: 'Bulk email allowed again',
  mail_reply_received: 'Reply received'
};

/**
 * A mail event's line (SR-523 stage 3): the email — its template, survey and version, and kind —
 * and why it was not sent; a bounce's error; where an unsubscribe came from and its note; a
 * reply's subject, opened in a drawer. A detail of another shape draws nothing here; the stored
 * event below says it whole.
 */
function MailEvent({ row, onReply }: { row: IAdminActivityRow; onReply: (replyId: string) => void }) {
  if (!row.kind.startsWith('mail_')) return null;
  const detail = detailOf(row);
  if (detail === null) return null;
  const text = (key: string) => (typeof detail[key] === 'string' ? (detail[key] as string) : null);
  if (row.kind === 'mail_reply_received') {
    const replyId = text('replyId');
    return (
      <p className="mb-1 flex flex-wrap items-baseline gap-2 text-sm" data-mail-reply>
        <span className="break-words">{text('subject') ?? '(no subject)'}</span>
        {replyId && (
          <Button type="button" variant="link" size="sm" className="h-auto p-0" onClick={() => onReply(replyId)}>
            Read the reply
          </Button>
        )}
      </p>
    );
  }
  if (row.kind === 'mail_suppressed' || row.kind === 'mail_suppression_cleared') {
    const source = text('source');
    return (
      <p className="mb-1 text-sm" data-mail-suppression>
        {source && sourceLabel(source)}
        {text('note') && <span className="block break-words text-muted-foreground">{text('note')}</span>}
      </p>
    );
  }
  if (row.kind === 'mail_bounced') {
    return (
      <p className="mb-1 text-sm" data-mail-bounce>
        {detail.hard === true ? 'Undeliverable address' : 'Temporary failure'}
        {text('error') && <span className="text-muted-foreground"> · {text('error')}</span>}
      </p>
    );
  }
  const name = text('templateName') ?? text('template');
  const reason = text('reason');
  const campaignId = text('campaignId');
  return (
    <p className="mb-1 text-sm" data-mail-event>
      {name}
      {(text('survey') || typeof detail.version === 'number') && (
        <span className="text-muted-foreground">
          {' '}
          · {[text('survey'), typeof detail.version === 'number' ? `v${detail.version}` : null].filter(Boolean).join(' · ')}
        </span>
      )}
      {text('kind') && <span className="text-muted-foreground"> · {mailKindLabel(text('kind') ?? '')}</span>}
      {reason && <span className="block text-muted-foreground">{reasonWords(reason)}</span>}
      {campaignId && (
        <Link
          to={mailLogPath({ tab: 'campaigns', campaign: campaignId })}
          className="block text-xs text-[var(--color-sapphire)] hover:underline"
        >
          The campaign
        </Link>
      )}
    </p>
  );
}

const ADDRESS_ROLES: Record<string, string> = { login: 'Sign-in', office: 'Office', cc: 'CC' };

/**
 * The record's email addresses and whether bulk email reaches each (SR-523 stage 3,
 * `GET …/mail-status`): a suppressed one says since when and why, and Clear — `mail.outreach.send`,
 * with a note — lets bulk email reach it again. Read with the feed, again after every write. Kept
 * out of the record's header, which SR-525 owns.
 */
function MailStrip({ participantId, written }: { participantId: string; written: number }) {
  const can = useCan();
  const [bump, setBump] = React.useState(0);
  const read = useAdminData<IParticipantMailStatus>(
    (signal) => mailLogApi.mailStatus(participantId, signal),
    [participantId, written, bump]
  );
  const [clearing, setClearing] = React.useState<Nullable<{ id: string; address: string; source: string }>>(null);
  if (read.error) {
    return <p className="text-xs text-muted-foreground">The email addresses could not be read: {read.error}</p>;
  }
  const addresses = read.data?.addresses ?? [];
  if (addresses.length === 0) return null;
  return (
    <div
      role="group"
      aria-label="Email delivery"
      className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-md border border-border px-3 py-2 text-[0.8125rem]"
      data-mail-strip=""
    >
      <span className="text-xs font-medium text-muted-foreground">Email delivery</span>
      {addresses.map((entry) => (
        <span key={`${entry.role}:${entry.address}`} className="flex flex-wrap items-center gap-1.5" data-mail-address={entry.address}>
          <span className="text-xs text-muted-foreground">{ADDRESS_ROLES[entry.role] ?? entry.role}</span>
          <span className="break-all">{entry.address}</span>
          {entry.suppression && (
            <>
              <span title={[sourceLabel(entry.suppression.source), entry.suppression.note].filter(Boolean).join(' — ')}>
                <StatusChip tone="attention">Unsubscribed {formatDateTime(entry.suppression.createdAt)}</StatusChip>
              </span>
              {can('mail.outreach.send') && (
                <Button
                  type="button"
                  variant="link"
                  size="sm"
                  className="h-auto p-0"
                  onClick={() =>
                    entry.suppression &&
                    setClearing({ id: entry.suppression.id, address: entry.address, source: entry.suppression.source })
                  }
                >
                  Clear…
                </Button>
              )}
            </>
          )}
        </span>
      ))}
      <ClearSuppressionDialog suppression={clearing} onClose={() => setClearing(null)} onDone={() => setBump((n) => n + 1)} />
    </div>
  );
}
